import React, { memo, useCallback, useState } from 'react';
import { locale } from '@tcc/i18n';
import classNames from 'classnames';
import { Button, Checkbox, Icon, Label, DENSITIES, STATUSES, TONES } from '@tcc/crate';

import ImageWithLoader from 'common/ImageWithLoader';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { getMessage } from 'util/locale';
import { getImageDataLayers, getSelectedLayerGroup, getSelectedLayers, getSelectedMapLayer } from './selectors';
import { CCILayer } from 'store/types/types';
import { formatDate } from 'util/formatters/dateFormatter';
import { setSelectedLayers } from 'manual';
import { getLayerMonth } from './layersParser';
import { getLayersGroupCheckBoxStates } from 'manual/util/zone-generation';
import { LAYERS_TYPES, MAX_SELECTED_LAYERS } from 'manual/constants';

interface CciImageItemProps {
  cciLayer: CCILayer;
  isDisabled: boolean;
  isSelected: boolean;
  selectCciLayer: (cciLayer?: CCILayer) => void;
  isManualFlowLowQualityLayers: boolean;
}

const CciImageItem = memo(function Item ({
  cciLayer,
  isDisabled,
  isSelected,
  selectCciLayer,
  isManualFlowLowQualityLayers
}: CciImageItemProps) {
  const isUnusable = !cciLayer.isUsable;
  const isLayerDisabled = (isUnusable && !isManualFlowLowQualityLayers) || isDisabled;
  const onClick = isLayerDisabled ? undefined : () => selectCciLayer(cciLayer);
  const layerLabelKey = isManualFlowLowQualityLayers
    ? 'label-text__generate-zones-low-quality-layer'
    : 'label-text__generate-zones-unusable-layer';
  const formattedEventDate = formatDate(cciLayer.eventDate, { locale });

  const liClassString = classNames({
    'cci-image-layer-item': true,
    isSelected,
    isDisabled: isLayerDisabled
  });

  const checkboxContent = (
    <>
      <ImageWithLoader src={cciLayer.imageryThumbnailUrl}/>
      <span>{formattedEventDate}</span>
    </>
  );

  return (
    <li className={liClassString}>
      <Checkbox
        checked={isSelected}
        onChange={onClick}
        disabled={isLayerDisabled}
        label={checkboxContent}
        testId={`select-layer-checkbox-${cciLayer.id}`}
      />
      {isUnusable && (
        <Label
          density={DENSITIES.NORMAL}
          status={STATUSES.DEFAULT}
          tone={TONES.LIGHT}
          className="layer-label"
        >
          {getMessage(layerLabelKey)}
        </Label>
      )}
    </li>
  );
});

interface MonthAccordionProps {
  month: string;
  cciLayers: CCILayer[];
  selectedLayersByMonth: CCILayer[];
  selectCciLayer: (value?: CCILayer | CCILayer[]) => void;
  allSelectedLayersCount: number;
  selectedMapType: typeof LAYERS_TYPES[keyof typeof LAYERS_TYPES];
  isManualFlowLowQualityLayers: boolean;
}

const MonthAccordion = memo(function Item({
  month,
  cciLayers,
  selectedLayersByMonth,
  selectCciLayer,
  allSelectedLayersCount = 0,
  selectedMapType,
  isManualFlowLowQualityLayers
}: MonthAccordionProps) {
  const [open, setOpen] = useState(false);
  const cciLayersCount = cciLayers.length;
  const usableCCiLayers = isManualFlowLowQualityLayers ? cciLayers : cciLayers.filter(({ isUsable }) => isUsable);

  const { isChecked: isMonthSelected, isDisabled, isIndeterminate } = getLayersGroupCheckBoxStates({
    allSelectedLayersCount,
    groupUsableLayersCount: usableCCiLayers.length,
    selectedGroupLayersCount: selectedLayersByMonth.length,
    selectedMapType
  });

  const classString = classNames({
    ['cci-image-group-item']: true,
    isOpen: open
  });

  return (
    <>
      <li
        key={month}
        className={classString}
        data-test-id={`cci-image-group-item-${month}`}
        onClick={() => setOpen(prevState => !prevState)}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isMonthSelected}
            disabled={isDisabled}
            isIndeterminate={isIndeterminate}
            onChange={() => selectCciLayer(usableCCiLayers)}
            testId={`select-all-checkbox-${month}`}
          />
        </div>
        {getMessage(`month--${month.toLowerCase()}`)} {`(${cciLayersCount})`}
        <Icon glyph={Icon.set.CARET_DOWN} />
      </li>
      {open && <ul>
        {cciLayers.map((cciLayer) => {
          const isSelected = selectedLayersByMonth.some(({ id }) => id === cciLayer.id);
          const isLayerDisabled = (allSelectedLayersCount === MAX_SELECTED_LAYERS[selectedMapType] && !isSelected);

          return (
            <CciImageItem
              cciLayer={cciLayer}
              key={cciLayer.id}
              isDisabled={isLayerDisabled}
              isSelected={isSelected}
              selectCciLayer={selectCciLayer}
              isManualFlowLowQualityLayers={isManualFlowLowQualityLayers}
            />
          );
        })}
      </ul>}
    </>
  );
});

interface ZoneGenerationCciImageSelectionProps {
  isManualFlowLowQualityLayers: boolean;
}

const ZoneGenerationCciImageSelection = ({ isManualFlowLowQualityLayers }: ZoneGenerationCciImageSelectionProps) => {
  const dispatch = useAppDispatch();
  const selectedLayerGroup = useAppSelector(getSelectedLayerGroup);
  const imageDataLayers = useAppSelector(getImageDataLayers(selectedLayerGroup));
  const selectedLayers = (useAppSelector(getSelectedLayers) || []) as CCILayer[];
  const { selectedMapType } = useAppSelector(getSelectedMapLayer);

  const selectedLayersCount = selectedLayers?.length;

  const selectCciLayer = useCallback((value?: CCILayer | CCILayer[]) => {
    dispatch(setSelectedLayers(value));
  }, [dispatch]);

  if (!imageDataLayers.length) return null;

  return (
    <>
      <Button
        mode="link"
        data-test-id="deselect-all-layers"
        disabled={!selectedLayersCount}
        onClick={() => selectCciLayer()}
      >
        {getMessage('button-text__generate-zones-layers-deselect-all', {
          selectedLayers: selectedLayersCount.toString()
        })}
      </Button>
      <section data-test-id="layers-groups-header">
        {getMessage('label-text__generate-zones-layers-cci-image-data')}
      </section>
      <ul data-test-id="cci-image-group-list">
        {imageDataLayers.map(([month, cciLayers]) => {
          const selectedLayersByMonth = selectedLayers.filter((cciLayer: CCILayer) => {
            return getLayerMonth(cciLayer) === month && cciLayer.year.toString() === selectedLayerGroup;
          });

          return (
            <MonthAccordion
              key={`${selectedLayerGroup}-${month}`}
              cciLayers={cciLayers}
              month={month}
              allSelectedLayersCount={selectedLayersCount}
              selectedLayersByMonth={selectedLayersByMonth}
              selectCciLayer={selectCciLayer}
              selectedMapType={selectedMapType}
              isManualFlowLowQualityLayers={isManualFlowLowQualityLayers}
            />
          );
        })}
      </ul>
    </>
  );
};

export default memo(ZoneGenerationCciImageSelection);
